import React, { useEffect, useState } from "react";
import { Plus, Calendar, X, UserPlus } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { PageHeader, Card, Badge, Empty, RedButton, GhostButton, Field, inputCls } from "../../components/crm/ui";
import { toast } from "sonner";

export default function Events() {
  const [events, setEvents] = useState([]);
  const [show, setShow] = useState(false);
  const [attend, setAttend] = useState(null);
  const load = () => api.get("/events").then(({ data }) => setEvents(data.items)).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <div>
      <PageHeader title="Events" subtitle="Services, outreach, and gatherings — attendance updates contact timelines.">
        <RedButton onClick={() => setShow(true)} data-testid="event-create"><Plus size={15} /> Create Event</RedButton>
      </PageHeader>
      {events.length === 0 ? (
        <Card className="p-6"><Empty icon={Calendar} title="No events yet" hint="Create an event to track registration and attendance." action={<RedButton onClick={() => setShow(true)}><Plus size={15} /> Create Event</RedButton>} /></Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {events.map((e) => (
            <Card key={e.id} className="p-5" data-testid={`event-card-${e.id}`}>
              <div className="flex items-start justify-between">
                <div><h3 className="font-display font-bold text-slate-900">{e.name}</h3><p className="text-xs text-slate-500 mt-0.5">{e.type} · {e.date}</p></div>
                <Badge tone="blue">{e.attendance_count || 0}</Badge>
              </div>
              {e.location && <p className="text-sm text-slate-600 mt-2">{e.location}</p>}
              <RedButton className="w-full mt-4 !py-2" onClick={() => setAttend(e)} data-testid={`event-attend-${e.id}`}><UserPlus size={14} /> Add Visitor / Attendance</RedButton>
            </Card>
          ))}
        </div>
      )}
      {show && <EventModal onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
      {attend && <AttendModal event={attend} onClose={() => setAttend(null)} onSaved={() => { setAttend(null); load(); }} />}
    </div>
  );
}

function EventModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", type: "Service", date: new Date().toISOString().slice(0,10), location: "", description: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => { if (!form.name) { toast.error("Name required"); return; } try { await api.post("/events", form); toast.success("Event created"); onSaved(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5"><h2 className="font-display text-xl font-bold">Create Event</h2><button onClick={onClose} className="text-slate-400"><X size={20} /></button></div>
        <div className="space-y-3">
          <Field label="Name"><input className={inputCls} value={form.name} onChange={set("name")} data-testid="event-name" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type"><select className={inputCls} value={form.type} onChange={set("type")} data-testid="event-type">{["Service","Outreach","Bible Study","Revival","Special Service","Meeting","Other"].map((t) => <option key={t}>{t}</option>)}</select></Field>
            <Field label="Date"><input type="date" className={inputCls} value={form.date} onChange={set("date")} data-testid="event-date" /></Field>
          </div>
          <Field label="Location"><input className={inputCls} value={form.location} onChange={set("location")} data-testid="event-location" /></Field>
        </div>
        <div className="flex gap-3 mt-6"><GhostButton className="flex-1" onClick={onClose}>Cancel</GhostButton><RedButton className="flex-1" onClick={save} data-testid="event-save">Create</RedButton></div>
      </Card>
    </div>
  );
}

function AttendModal({ event, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", first_time: false, invited_by: "", status: "Attended" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => { if (!form.name) { toast.error("Name required"); return; } try { const { data } = await api.post(`/events/${event.id}/attendance`, form); toast.success(data.created ? "New contact added" : "Attendance recorded (existing contact)"); onSaved(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1"><h2 className="font-display text-xl font-bold">Add Attendance</h2><button onClick={onClose} className="text-slate-400"><X size={20} /></button></div>
        <p className="text-sm text-slate-500 mb-5">{event.name} — we'll match existing contacts automatically.</p>
        <div className="space-y-3">
          <Field label="Name"><input className={inputCls} value={form.name} onChange={set("name")} data-testid="attend-name" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone"><input className={inputCls} value={form.phone} onChange={set("phone")} data-testid="attend-phone" /></Field>
            <Field label="Email"><input className={inputCls} value={form.email} onChange={set("email")} data-testid="attend-email" /></Field>
          </div>
          <Field label="Invited by"><input className={inputCls} value={form.invited_by} onChange={set("invited_by")} data-testid="attend-invited" /></Field>
          <label className="flex items-center gap-2.5 text-sm text-slate-700"><input type="checkbox" checked={form.first_time} onChange={(e) => setForm({ ...form, first_time: e.target.checked })} className="accent-red-600" data-testid="attend-firsttime" /> First-time visitor</label>
        </div>
        <div className="flex gap-3 mt-6"><GhostButton className="flex-1" onClick={onClose}>Cancel</GhostButton><RedButton className="flex-1" onClick={save} data-testid="attend-save">Record</RedButton></div>
      </Card>
    </div>
  );
}
